import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { PackageModel } from '@data-layer/models/lcl';
import { ReplaySubject } from 'rxjs';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { enumLengthUnit, enumWeightUnit } from '@data-layer/enums/common';
import { LclContainerService } from '../../services';
import { PackageData } from '@app/modules/lcl-shipping/types';
import { ManagementDraftPackageModel, ManagementPackageModel } from '@data-layer/models/management';
import { takeUntil } from 'rxjs/operators';
import { LclCreateQuotePackagesComponent } from '@shared/lcl-mutual/components';

@Component({
  selector: 'app-lcl-edit-package-sidebar',
  templateUrl: './lcl-edit-package.component.html',
  styleUrls: ['./lcl-edit-package.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEditPackageComponent implements OnInit, OnDestroy {
  @Input() public packages: Array<PackageModel | ManagementPackageModel | ManagementDraftPackageModel>;
  @Input() public isAdminView: boolean;
  @Input() public isUpdatedLayout: boolean;
  @Input() public isShowAdd: boolean;
  @Input() public isShowClear: boolean;
  @Output() public dataSaved: EventEmitter<PackageModel[] | ManagementDraftPackageModel[]> = new EventEmitter<
    PackageModel[] | ManagementDraftPackageModel[]
  >();

  @ViewChild('packagesView', { static: false }) public packagesView: LclCreateQuotePackagesComponent;

  public packagesFormArray: UntypedFormArray;
  public weightSwitchControl: UntypedFormControl = new UntypedFormControl(false);

  private oldUnitValues: { lengthUnit: enumLengthUnit; weightUnit: enumWeightUnit } = {
    lengthUnit: null,
    weightUnit: null,
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private fh: FormHelper, private fb: UntypedFormBuilder, private lclContainerService: LclContainerService) {}

  public ngOnInit(): void {
    this.packagesFormArray = this.fb.array([]);
    const packages: Array<PackageModel | ManagementPackageModel | ManagementDraftPackageModel> =
      this.lclContainerService.convertPackagesDataForForm(this.packages);
    this.packages.forEach(() => {
      this.packagesFormArray.push(this.fh.createPackageFormGroup());
    });

    this.oldUnitValues.lengthUnit = packages[0]?.lengthUnit;
    this.oldUnitValues.weightUnit = packages[0]?.weightUnit;

    this.packagesFormArray.patchValue(packages);
    this.weightSwitchControl.setValue(packages[0]?.lengthUnit === enumLengthUnit.INCH && packages[0]?.weightUnit === enumWeightUnit.LB);
    this.weightSwitchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.packagesFormArray.controls.forEach((item: AbstractControl) =>
        item.patchValue(
          val
            ? { weightUnit: enumWeightUnit.LB, lengthUnit: enumLengthUnit.INCH }
            : { weightUnit: enumWeightUnit.KG, lengthUnit: enumLengthUnit.CENTIMETER },
          { emitEvent: false },
        ),
      );
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSubmit(): void {
    if (this.packagesFormArray.invalid) {
      this.fh.markAsTouched(this.packagesFormArray);
      this.packagesView.forceUpdateForm();
      return;
    }

    const data = this.packagesFormArray.value.map((item) => {
      if (!item.id) {
        delete item.id;
      }

      return item;
    });

    this.dataSaved.emit(data);
  }

  public addPackage(): void {
    const firstPackage = this.packagesFormArray.getRawValue()[0];

    this.packagesFormArray.push(this.fh.createPackageFormGroup(firstPackage?.lengthUnit, firstPackage?.weightUnit));
  }

  public onRemovePackage(index: number): void {
    this.packagesFormArray.removeAt(index);
  }

  public onFormUpdated(data: PackageData): void {
    for (const item of this.packagesFormArray.controls) {
      // imperial - metric dropdown update
      // imperial <-> metric dropdown update
      if (this.oldUnitValues.lengthUnit !== data.lengthUnit) {
        data.weightUnit = data.lengthUnit === enumLengthUnit.CENTIMETER ? enumWeightUnit.KG : enumWeightUnit.LB;
      }
      if (this.oldUnitValues.weightUnit !== data.weightUnit) {
        data.lengthUnit = data.weightUnit === enumWeightUnit.KG ? enumLengthUnit.CENTIMETER : enumLengthUnit.INCH;
      }
      item.patchValue({ lengthUnit: data.lengthUnit, weightUnit: data.weightUnit }, { emitEvent: false });
    }

    this.weightSwitchControl.patchValue(data?.weightUnit !== enumWeightUnit.KG);

    this.oldUnitValues.lengthUnit = data.lengthUnit;
    this.oldUnitValues.weightUnit = data.weightUnit;

    this.packagesFormArray.markAsTouched();
  }
}
